import { CheckCircle2, Download, Loader2, Upload } from "lucide-react";

export default function PatientPaymentLinkCard({
  link,
  uploading = false,
  uploadError = null,
  fileRef,
  onDownloadQR,
  onPickFile,
  onFileChosen,
}) {
  const submitted = link?.status === "proof_submitted";
  const isArLink = !!link?.ar_receivable_id || !!link?.ar_number;
  const amount = (Number(link?.amount_centavos || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div className="w-full max-w-md bg-white rounded-2xl shadow-sm p-6">
      <div className="text-center">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">Payment due to</p>
        <h1 className="text-xl font-bold text-slate-900 mt-1">{link?.clinic_name}</h1>
        <p className="mt-3 text-3xl font-bold text-slate-900 tabular-nums">
          ₱{amount}
        </p>
        {isArLink ? (
          <div className="mt-3 rounded-xl border border-teal-200 bg-teal-50 px-3 py-2 text-sm text-teal-950">
            <p className="font-semibold">{link.ar_number || "Outstanding balance"}</p>
            <p className="mt-0.5 text-xs text-teal-800">
              {link.context_label || "Outstanding patient balance"}
            </p>
          </div>
        ) : null}
      </div>

      {link?.qr_image_url ? (
        <div className="mt-6">
          <div className="bg-white border border-slate-200 rounded-xl p-4 max-w-sm mx-auto">
            <img src={link.qr_image_url} alt="Payment QR"
              className="w-full aspect-square object-contain rounded-lg" />
            {link.qr_label ? (
              <p className="mt-2 text-center text-sm text-slate-500">{link.qr_label}</p>
            ) : null}
          </div>
          <div className="mt-3 text-center">
            <button onClick={onDownloadQR}
              data-testid="pay-download-qr"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded">
              <Download className="w-3.5 h-3.5" />Download QR
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 text-center">
          The clinic didn't attach a QR. Please contact them for payment instructions.
        </div>
      )}

      <div className="mt-8 border-t border-slate-100 pt-6">
        {submitted ? (
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-5 text-center">
            <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500" />
            <p className="mt-2 text-sm font-semibold text-emerald-900">Sent. Waiting for the clinic to verify.</p>
            <p className="text-xs text-emerald-800 mt-1">You can close this page. The clinic will follow up if anything's needed.</p>
            <button onClick={onPickFile}
              data-testid="pay-replace-proof"
              className="mt-4 text-xs font-semibold text-emerald-800 underline">
              Upload a different file
            </button>
            <input ref={fileRef} type="file" accept="image/*"
              onChange={onFileChosen} className="hidden" />
          </div>
        ) : (
          <>
            <p className="text-center text-sm text-slate-700">
              <span className="font-semibold">Step 2.</span> Upload a screenshot of your payment receipt.
            </p>
            <div className="mt-3">
              <button onClick={onPickFile} disabled={uploading}
                data-testid="pay-upload"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-60">
                {uploading
                  ? <><Loader2 className="w-4 h-4 animate-spin" />Uploading…</>
                  : <><Upload className="w-4 h-4" />Upload payment proof</>}
              </button>
              <input ref={fileRef} type="file" accept="image/*"
                onChange={onFileChosen} className="hidden" />
            </div>
            {uploadError ? (
              <p className="mt-2 text-xs text-rose-700 text-center" role="alert">{uploadError}</p>
            ) : null}
            <p className="mt-3 text-[11px] text-slate-400 text-center">
              Image only · Max 5 MB · Stays private to the clinic.
            </p>
          </>
        )}
      </div>

      <div className="mt-8 text-center">
        <p className="text-[11px] text-slate-400">Secured by Dental OS</p>
      </div>
    </div>
  );
}
